import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, GraduationCap, School, Layers } from "lucide-react";

export default function NextStepCTA() {
    return (
        <section
            className="relative py-24 sm:py-32 overflow-hidden bg-gradient-to-b from-white via-ailavender/20 to-white"
            data-testid="next-step-section"
        >
            {/* Decorative soft blurred background blobs */}
            <div className="absolute top-1/4 left-10 w-[400px] h-[400px] rounded-full blur-3xl opacity-25 bg-gradient-to-tr from-aiblue to-aisky pointer-events-none" />
            <div className="absolute bottom-1/4 right-10 w-[450px] h-[450px] rounded-full blur-3xl opacity-20 bg-gradient-to-bl from-aiorange to-aipeach pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">

                    {/* Left Column: Information & CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-7 space-y-6"
                    >
                        <span
                            className="inline-block text-xs uppercase tracking-[0.3em] text-aiorange font-bold bg-aiorange/10 px-4 py-1.5 rounded-full"
                            data-testid="next-step-badge"
                        >
                            Know Your Next Step
                        </span>

                        <h2
                            className="font-display font-black text-4xl sm:text-5xl tracking-normal leading-tight text-gray-900"
                            data-testid="next-step-headline"
                        >
                            Moving from Intermediate <br />
                            to <span className="text-aiblue">B.Tech Engineering?</span>
                        </h2>

                        <p className="font-sans text-base sm:text-lg leading-relaxed text-gray-600 max-w-xl">
                            The transition from Intermediate to engineering is the most critical milestone of your academic journey. Don't leave your career choices to chance. Map your options based on your EAMCET rank, budget, and location preferences using our personalized guidance system.
                        </p>

                        {/* Feature Highlights Grid */}
                        <div className="grid sm:grid-cols-2 gap-6 pt-4">
                            <div className="flex gap-3 items-start">
                                <div className="p-2.5 rounded-xl bg-aiblue/10 text-aiblue shrink-0">
                                    <Layers className="h-5 w-5" />
                                </div>
                                <div>
                                    <h4 className="font-sans font-bold text-gray-900 text-sm">Stream & Branch Fit</h4>
                                    <p className="text-xs text-gray-500 mt-0.5">Map your MPC or Diploma background to the right engineering specialization.</p>
                                </div>
                            </div>

                            <div className="flex gap-3 items-start">
                                <div className="p-2.5 rounded-xl bg-aiorange/10 text-aiorange shrink-0">
                                    <School className="h-5 w-5" />
                                </div>
                                <div>
                                    <h4 className="font-sans font-bold text-gray-900 text-sm">College Predictor</h4>
                                    <p className="text-xs text-gray-500 mt-0.5">Get recommended list of colleges matching your rank and location.</p>
                                </div>
                            </div>

                            <div className="flex gap-3 items-start">
                                <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-600 shrink-0">
                                    <Compass className="h-5 w-5" />
                                </div>
                                <div>
                                    <h4 className="font-sans font-bold text-gray-900 text-sm">Personal Roadmap</h4>
                                    <p className="text-xs text-gray-500 mt-0.5">AI-backed insight reports detailing curriculum readiness.</p>
                                </div>
                            </div>

                            <div className="flex gap-3 items-start">
                                <div className="p-2.5 rounded-xl bg-green-500/10 text-green-600 shrink-0">
                                    <GraduationCap className="h-5 w-5" />
                                </div>
                                <div>
                                    <h4 className="font-sans font-bold text-gray-900 text-sm">100% Free Guidance</h4>
                                    <p className="text-xs text-gray-500 mt-0.5">Instant report generation with zero hidden costs or fees.</p>
                                </div>
                            </div>
                        </div>

                        {/* CTA button */}
                        <div className="pt-6">
                            <Link
                                to="/career-guidance"
                                className="inline-flex items-center gap-2 bg-aiblue hover:bg-blue-700 text-white font-sans font-semibold py-4 px-8 rounded-full text-base transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-aiblue/20 group hover:scale-[1.02] active:scale-[0.98] cta-pulse"
                                data-testid="next-step-cta-btn"
                            >
                                Get Free Career Guidance
                                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>
                    </motion.div>

                    {/* Right Column: Recommended Colleges Preview (Matches actual page styling) */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        whileInView={{ opacity: 1, scale: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="lg:col-span-5 relative"
                    >
                        {/* Container simulating the Recommended Colleges section */}
                        <div className="bg-slate-50 border border-gray-100 rounded-[2.5rem] p-6 sm:p-8 shadow-2xl relative z-10 hover:shadow-aiblue/5 transition duration-300">

                            {/* Blue badge header */}
                            <div className="flex justify-center mb-4">
                                <span className="inline-flex items-center gap-1.5 bg-aiblue text-white text-[11px] font-bold px-4 py-1.5 rounded-full shadow-sm">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                    Colleges Matched
                                </span>
                            </div>

                            <h3 className="text-center font-display font-black text-xl sm:text-2xl text-gray-900 leading-tight">
                                Your Recommended Colleges
                            </h3>
                            <p className="text-center text-[10px] text-gray-400 font-medium mt-0.5 tracking-wider uppercase">
                                Rank 20000 · OC category · CSE
                            </p>

                            {/* College Cards Stack / Grid */}
                            <div className="space-y-4 mt-6">
                                {/* College Card 1: Mallareddy Engineering College */}
                                <div className="bg-white border-2 border-emerald-500/20 rounded-2xl p-5 shadow-md relative overflow-hidden transition-transform duration-300 hover:scale-[1.01]">
                                    <div className="flex items-center gap-1.5 mb-2.5">
                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                        <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider bg-emerald-50 px-2 py-0.5 rounded-full">
                                            SAFE
                                        </span>
                                    </div>
                                    <h4 className="font-sans font-extrabold text-gray-900 text-[13px] leading-tight mb-3">
                                        Mallareddy Engineering College
                                    </h4>

                                    <div className="grid grid-cols-2 gap-y-2 text-[11px] border-t border-gray-50 pt-2.5">
                                        <div>
                                            <span className="text-gray-400 font-medium block">Place</span>
                                            <span className="text-gray-700 font-bold">Maisammaguda</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400 font-medium block">Affiliated to</span>
                                            <span className="text-gray-700 font-bold">JNTUH</span>
                                        </div>
                                        <div className="col-span-2">
                                            <span className="text-gray-400 font-medium block">Branch</span>
                                            <span className="text-gray-700 font-bold line-clamp-1">Computer Science & Eng. (AI&ML)</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400 font-medium block">Fee</span>
                                            <span className="text-emerald-600 font-extrabold text-xs">₹1,15,000/yr</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* View All Button */}
                            <div className="mt-6 flex justify-center">
                                <Link
                                    to="/career-guidance"
                                    className="inline-flex items-center gap-1.5 bg-aiblue hover:bg-blue-700 text-white font-sans text-xs font-bold px-6 py-3 rounded-full transition-all duration-300 shadow-md hover:shadow-lg group"
                                    data-testid="next-step-colleges-view-all"
                                >
                                    View All Matches
                                    <span className="bg-white/20 text-white px-1.5 py-0.5 rounded-md text-[9px] font-extrabold group-hover:bg-white/30 transition-colors">
                                        30+
                                    </span>
                                    <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                                </Link>
                            </div>
                        </div>

                        {/* Floating elements behind/around card */}
                        <div className="absolute -top-6 -right-6 h-24 w-24 bg-aipeach/40 rounded-full blur-xl -z-10" />
                        <div className="absolute -bottom-6 -left-6 h-28 w-28 bg-ailavender/40 rounded-full blur-xl -z-10" />
                    </motion.div>

                </div>
            </div>
        </section>
    );
}
